import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Building, Clock, Send, Paperclip, ArrowLeft, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const ContactPage = () => {
  const navigate = useNavigate();
  const [contactForm, setContactForm] = useState({
    name: '',
    organization: '',
    subject: '',
    message: '',
    file: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.subject || !contactForm.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Create mailto link
      const subject = encodeURIComponent(`[TabedLab ${contactForm.subject}] from ${contactForm.name}`);
      const body = encodeURIComponent(
        `Name: ${contactForm.name}\nOrganization: ${contactForm.organization || 'N/A'}\nSubject: ${contactForm.subject}\n\nMessage:\n${contactForm.message}`
      );
      
      window.location.href = `mailto:support@tabedlab.com?subject=${subject}&body=${body}`;
      
      toast.success('Opening email client...');
      setContactForm({ name: '', organization: '', subject: '', message: '', file: null });
    } catch (error) {
      toast.error('Failed to submit form. Please email us directly at support@tabedlab.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEO 
        title="Contact Us"
        description="Get in touch with the TabedLab team. We're here to help with questions about our AI-powered spectral analysis platform."
        keywords="contact TabedLab, spectroscopy support, laboratory analysis help, customer service"
        url="/contact"
      />
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <img 
              src="/tabedlab-logo.png" 
              alt="TabedLab Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              TabedLab
            </span>
          </div>
          
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <Mail className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-cyan-300">Get in Touch</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="text-white">Contact </span>
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Us</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Have questions about TabedLab? Want to discuss enterprise pricing or partnerships? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Info */}
          <div>
            <h2 className="text-2xl font-semibold text-white mb-6">How can we help?</h2>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-4 p-5 bg-slate-800/50 border border-slate-700 rounded-xl">
                <div className="w-14 h-14 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <Mail className="w-7 h-7 text-cyan-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Email us at</p>
                  <a href="mailto:support@tabedlab.com" className="text-lg text-white font-medium hover:text-cyan-400 transition-colors">
                    support@tabedlab.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 bg-slate-800/50 border border-slate-700 rounded-xl">
                <div className="w-14 h-14 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Clock className="w-7 h-7 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Response time</p>
                  <p className="text-lg text-white font-medium">Within 24 hours</p>
                </div>
              </div>

              <div className="flex items-center gap-4 p-5 bg-slate-800/50 border border-slate-700 rounded-xl">
                <div className="w-14 h-14 rounded-lg bg-purple-500/10 flex items-center justify-center">
                  <MessageSquare className="w-7 h-7 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-slate-400">Looking for quick answers?</p>
                  <button 
                    onClick={() => navigate('/faq')}
                    className="text-lg text-white font-medium hover:text-purple-400 transition-colors"
                  >
                    Check our FAQ →
                  </button>
                </div>
              </div>
            </div>

            {/* What to Expect */}
            <div className="p-6 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl">
              <h3 className="font-semibold text-white mb-3">What to expect:</h3>
              <ul className="space-y-2 text-slate-400">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                  Quick response within 24 hours
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                  Personalized support from our team
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400"></div>
                  Expert guidance on spectral analysis
                </li>
              </ul>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
            <h2 className="text-xl font-semibold text-white mb-6">Send us a message</h2>
            
            <form onSubmit={handleContactSubmit} className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                  placeholder="First & Last Name"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
                  required
                />
              </div>

              {/* Organization */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Organization
                </label>
                <div className="relative">
                  <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    value={contactForm.organization}
                    onChange={(e) => setContactForm({...contactForm, organization: e.target.value})}
                    placeholder="University, Private Lab, Company..."
                    className="w-full pl-11 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>
              </div>

              {/* Subject */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Subject <span className="text-red-400">*</span>
                </label>
                <select
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors appearance-none cursor-pointer"
                  required
                >
                  <option value="" className="text-slate-500">Select a subject...</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Partnership Inquiry">Partnership Inquiry</option>
                  <option value="Enterprise Pricing">Enterprise Pricing</option>
                  <option value="Feature Request">Feature Request</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                  placeholder="How can we help you?"
                  rows={5}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                  required
                />
              </div>

              {/* File Upload (Optional) */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Attachment <span className="text-slate-500">(Optional)</span>
                </label>
                <div className="relative">
                  <input
                    type="file"
                    onChange={(e) => setContactForm({...contactForm, file: e.target.files[0]})}
                    className="hidden"
                    id="contact-file"
                    accept=".csv,.xlsx,.pdf,.png,.jpg,.jpeg"
                  />
                  <label
                    htmlFor="contact-file"
                    className="flex items-center gap-3 px-4 py-3 bg-slate-900 border border-slate-700 border-dashed rounded-lg cursor-pointer hover:border-slate-600 transition-colors"
                  >
                    <Paperclip className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-400 text-sm">
                      {contactForm.file ? contactForm.file.name : "Having trouble with a file? Upload a sample (redact sensitive info)"}
                    </span>
                  </label>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-6 text-lg rounded-xl"
              >
                {isSubmitting ? (
                  <span>Sending...</span>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 px-6 mt-16">
        <div className="max-w-5xl mx-auto text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} TabedLab. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
